import React, { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useStudentsByGroup,
} from '../../shared/hooks';
import { Icons, LabeledSwitchButton } from '../../shared/components';
import { RefinedTargetAudience } from './new-feedback/RefinedTargetAudience';
import { TargetedStudentData, TargetedStudents } from '../types';
import { FeedbackInfoToggleWrapper, FeedbackTargetAudienceWrapper } from './Atoms';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { IGroupStudent } from 'src/config/api/models/User';

type FeedbackTargetAudienceProps = {
  readOnly?: boolean;
  isRefined?: boolean;
  onChangeRefinement?: (newValue: boolean) => void;
  targetedStudents?: TargetedStudents;
  onUpdateTargetedStudents: (targetedStudents: TargetedStudents) => void;
};

export const FeedbackTargetAudience: FC<FeedbackTargetAudienceProps> = ({
  readOnly,
  isRefined,
  onChangeRefinement,
  targetedStudents,
  onUpdateTargetedStudents,
}) => {
  const { t } = useTranslation();
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const selectedStudent = useAppSelector((state) => state.filters.selectedStudent);
  const { data: studentsData } = useStudentsByGroup(selectedGroup || '');

  const initializeTargetAudience = useCallback(
    (students: IGroupStudent[]) => {
      const initialValue: TargetedStudents = {};
      students.forEach((student) => {
        initialValue[student.id] = { student, value: true };
      });
      onUpdateTargetedStudents(initialValue);
    },
    [onUpdateTargetedStudents]
  );

  useEffect(() => {
    if (studentsData != null) {
      initializeTargetAudience(studentsData.data);
    }
  }, [initializeTargetAudience, studentsData]);

  const handleRefineStudent = useCallback(
    (targetedStudent: TargetedStudentData, newValue: boolean) => {
      onUpdateTargetedStudents({
        ...targetedStudents,
        [targetedStudent.student.id]: {
          student: targetedStudent.student,
          value: newValue,
        },
      });
    },
    [targetedStudents, onUpdateTargetedStudents]
  );

  return targetedStudents != null ? (
    <FeedbackTargetAudienceWrapper>
      <FeedbackInfoToggleWrapper>
        {!readOnly && isRefined != null && selectedStudent === '' && (
          <LabeledSwitchButton
            icon={Icons.COLLABORATION}
            checked={isRefined}
            onChange={onChangeRefinement ? onChangeRefinement : undefined}
            label={t('feedback.targetAudience.refine')}
          />
        )}
      </FeedbackInfoToggleWrapper>
      {!readOnly && isRefined ? (
        <RefinedTargetAudience
          targetedStudents={targetedStudents}
          onRefineStudent={handleRefineStudent}
        />
      ) : null}
    </FeedbackTargetAudienceWrapper>
  ) : null;
};
